const MAX_CAPTURE_TITLE = 120;
const ELEMENT_NAMES = {
    a: 'Link', button: 'Button', img: 'Image', picture: 'Image', svg: 'Icon',
    input: 'Input', textarea: 'Text field', select: 'Dropdown', label: 'Label',
    nav: 'Navigation', header: 'Header', footer: 'Footer', main: 'Main content',
    h1: 'Heading', h2: 'Heading', h3: 'Heading', h4: 'Heading', h5: 'Heading', h6: 'Heading',
    p: 'Text', span: 'Text', ul: 'List', ol: 'List', li: 'List item', table: 'Table',
    form: 'Form', section: 'Section', article: 'Article', div: 'Element',
    progress: 'Progress bar', progressbar: 'Progress bar', meter: 'Meter',
    checkbox: 'Checkbox', radio: 'Radio button', slider: 'Slider', switch: 'Switch',
    textbox: 'Text field', combobox: 'Dropdown', listbox: 'List', menu: 'Menu',
    menuitem: 'Menu item', tab: 'Tab', tabpanel: 'Tab panel', dialog: 'Dialog',
    video: 'Video', audio: 'Audio', canvas: 'Canvas', iframe: 'Frame',
    frame: 'Frame', group: 'Group', component: 'Component', instance: 'Component instance',
    component_set: 'Component set', rectangle: 'Rectangle', ellipse: 'Ellipse',
    text: 'Text', vector: 'Vector', line: 'Line', polygon: 'Polygon', star: 'Star',
};
const clean = (value) => typeof value === 'string' ? value.replace(/\s+/g, ' ').trim() : '';
const usefulName = (value) => {
    const text = clean(value);
    // A changing value such as 45% or 3 / 10 does not identify the element.
    return /[^\s\d.,%+−\-–—/:()[\]]/.test(text) ? text : '';
};
const shorten = (value) => value.length <= MAX_CAPTURE_TITLE
    ? value
    : `${value.slice(0, MAX_CAPTURE_TITLE - 1).replace(/\s+$/, '')}…`;
export function isPlaceholderIssueTitle(title) {
    return /^(?:new issue(?:\s*·\s*["“].*["”])?)?$/i.test(clean(title));
}
/** Reserved/blank edits do not rename saved issues. With no accepted title,
 * undefined asks deriveIssueTitle to use the captured element instead. */
export function normalizeIssueTitleEdit(title, acceptedTitle) {
    if (!isPlaceholderIssueTitle(title))
        return title.trim();
    return isPlaceholderIssueTitle(acceptedTitle) ? undefined : acceptedTitle.trim();
}
function pageName(input) {
    const title = usefulName(input.pageTitle) || usefulName(input.elementData?.titleContext?.pageTitle);
    if (title)
        return title;
    try {
        const url = new URL(input.pageUrl || '');
        const segments = url.pathname.split('/').filter(Boolean);
        const segment = segments[segments.length - 1];
        if (segment) {
            const readable = usefulName(decodeURIComponent(segment).replace(/\.[a-z0-9]+$/i, '').replace(/[-_]+/g, ' '));
            if (readable)
                return readable.charAt(0).toUpperCase() + readable.slice(1);
        }
        return url.hostname.replace(/^www\./, '') || 'Page';
    }
    catch {
        return 'Page';
    }
}
/** Keep an authored title; name an empty/legacy placeholder from captured context. */
export function deriveIssueTitle(input) {
    if (!isPlaceholderIssueTitle(input.title))
        return input.title.trim();
    const data = input.elementData || {};
    const context = data.titleContext || {};
    const fingerprint = data.fingerprint || {};
    const label = [
        context.accessibleName, fingerprint.ariaLabel,
        context.heading, context.altText, data.altText, context.title,
        context.text, fingerprint.directText, fingerprintText(fingerprint),
    ].map(usefulName).find(value => value && !isPlaceholderIssueTitle(value));
    if (label)
        return shorten(label);
    return shorten(`${pageName(input)} · ${elementName(context.role || fingerprint.role, data.tagName)}`);
}
// packages/extension/src/utils/elementFingerprint.ts MAX_INNER_HTML_LENGTH: a stored prefix this long may be cut.
const FINGERPRINT_INNER_HTML_LENGTH = 150;
const SHORT_LABEL = 40;
const htmlText = (value) => value.replace(/&nbsp;/g, ' ').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&');
/** element.textContent joins separate blocks with no space ("Issue typesAll time"), so an
 * element with children is named from the text nodes its stored innerHtml keeps apart. */
function fingerprintText(fingerprint) {
    if (!fingerprint.childCount)
        return fingerprint.textContent;
    const html = fingerprint.innerHtml || '';
    const parts = html.split(/<[^>]*(?:>|$)/);
    const cut = html.length >= FINGERPRINT_INNER_HTML_LENGTH;
    if (cut)
        parts.pop(); // the last text node may stop mid-word
    const texts = parts.map(part => usefulName(htmlText(part))).filter(Boolean);
    if (!cut)
        return texts.join(' ');
    if (texts.length)
        return texts[0];
    // shortcut: no text node survived the prefix (an icon's markup filled it); a short label
    // is almost always one node, a long one is likely glued. Upgrade by storing per-node text.
    const text = clean(fingerprint.textContent);
    return text.length <= SHORT_LABEL ? text : '';
}
function elementName(rawRole, rawTag) {
    const role = clean(rawRole).toLowerCase();
    const tag = clean(rawTag).toLowerCase();
    const elementType = ELEMENT_NAMES[role] || ELEMENT_NAMES[tag] || (tag ? `<${tag}> element` : 'Capture');
    return elementType;
}
/** Capture once, while the real element and its label references still exist. */
export function captureIssueTitleContext(element) {
    // Kept self-contained: the review bridge injects this exact function into
    // the captured page, where its module closure does not exist.
    const clean = (value) => typeof value === 'string' ? value.replace(/\s+/g, ' ').trim() : '';
    const usefulName = (value) => {
        const text = clean(value);
        return /[^\s\d.,%+−\-–—/:()[\]]/.test(text) ? text : '';
    };
    const shorten = (value) => value.length <= 120 ? value : `${value.slice(0, 119).replace(/\s+$/, '')}…`;
    const textAttribute = (node, name) => usefulName(node?.getAttribute(name));
    const doc = element.ownerDocument;
    const visibleText = (node) => {
        if (!node || node.closest('[hidden], [aria-hidden="true"]'))
            return '';
        const style = doc.defaultView?.getComputedStyle(node);
        if (style?.display === 'none' || style?.visibility === 'hidden')
            return '';
        const innerText = node.innerText;
        return usefulName(typeof innerText === 'string' ? innerText : node.textContent);
    };
    const root = element.getRootNode();
    const labelledBy = clean(element.getAttribute('aria-labelledby')).split(' ').filter(Boolean)
        .map(id => 'getElementById' in root
        ? root.getElementById(id)?.textContent
        : doc.getElementById(id)?.textContent)
        .map(clean).filter(Boolean).join(' ');
    const labels = element.labels;
    const nativeLabel = labels ? Array.from(labels).map(visibleText).filter(Boolean).join(' ') : '';
    const accessibleName = usefulName(labelledBy) || usefulName(element.getAttribute('aria-label'))
        || usefulName(nativeLabel) || visibleText(element.closest('label'));
    const heading = element.matches('h1,h2,h3,h4,h5,h6,[role="heading"]')
        ? visibleText(element)
        : Array.from(element.querySelectorAll('h1,h2,h3,h4,h5,h6,[role="heading"]')).map(visibleText).find(Boolean);
    const image = element.matches('img') ? element : element.querySelector('img');
    const context = {
        accessibleName: shorten(accessibleName),
        heading: heading ? shorten(heading) : undefined,
        altText: textAttribute(image, 'alt'),
        title: textAttribute(element, 'title'),
        text: shorten(visibleText(element)),
        pageTitle: clean(doc.title),
        role: clean(element.getAttribute('role')),
    };
    const result = {};
    for (const key of Object.keys(context)) {
        if (context[key])
            result[key] = context[key];
    }
    return result;
}
//# sourceMappingURL=issueTitle.js.map